import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StatusCode } from 'api/protocols';
import { PostCustomerImport as ClientPostCustomerImport } from 'api/rpc/2024-04/clientAdmin/customer/customer';
import { PostCustomerImport as FacilityPostCustomerImport } from 'api/rpc/2024-04/facilityAdmin/customer/customer';
import { PostStandaloneCustomerImport } from 'api/rpc/2024-04/masterAdmin/customer/customer';
import Callout from 'components/callout/Callout';
import Input from 'components/form/input';
import Sheet from 'components/sheet/Sheet';
import Portal from 'elements/Portal';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { t } from 'i18next';
import React, { useRef, useState } from 'react';
import { showError, showSuccess } from 'redux/actions/ui';
import validator from "validator";

interface IProps {
  type: "client" | "facility" | "standalone";
  open: boolean;
  onClose: () => void;
}

export default function ImportCustomers({ type, open, onClose }: IProps) {
  const dispatch = useAppDispatch();
  const fileUploadRef = useRef(null);

  const { masterClientStore } = useAppSelector(store => store);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  async function handleImport() {
    const formData = new FormData();

    formData.append("file", selectedFile);

    let importRes = null;
    if (type === "facility") {
      importRes = await FacilityPostCustomerImport(formData, true);
    } else if (type === "client") {
      importRes = await ClientPostCustomerImport(formData, true);
    } else if (type === "standalone") {
      formData.append('client_id', String(masterClientStore?.client?.id));
      formData.append('email', importEmail);
      importRes = await PostStandaloneCustomerImport(formData, true);
    }

    if (importRes.status !== StatusCode.OK) {
      dispatch(showError(importRes.message));
      return;
    }
    dispatch(showSuccess(t("client_side.customer.main.customers.005")));

    closeImport();
  }

  function handleFileSelect(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files;
    if (file && file.length > 0) {
      // The accept attribute only filters the file picker; dropped files bypass it
      if (!file[0].name.toLowerCase().endsWith(".csv")) {
        dispatch(showError(t("Only .csv files are accepted")));
        event.target.value = "";
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file[0]);
    }
  }

  function closeImport() {
    fileUploadRef.current.value = "";
    setSelectedFile(null);
    onClose();
  }

  const [importEmail, setImportEmail] = useState('');

  return <Portal isMounted={open}>
    <Sheet
      title={t("Select File")}
      supportText={"Client: " + masterClientStore?.client?.full_name}
      open={open}
      size="small"
      closable
      onCancel={closeImport}
      onOk={handleImport}
      okText={!selectedFile ? t("No file selected") : t("Import")}
      okDisabled={!selectedFile || (type === 'standalone' && !validator.isEmail(importEmail)) ? true : false}
    >
      {type === 'standalone' && <div className="mb-2">
        {t('Enter an email to receive an import summary')}

        <Input
          placeholder={t("email@example.com")}
          id="import-email"
          value={importEmail}
          onChange={e => setImportEmail(e.target.value)}
        />
      </div>}
      <Callout
        type="info"
        title={
          !selectedFile
            ? t("No file selected")
            : t("Selected File")
        }
        content={
          !selectedFile
            ? t("Select a file to continue")
            : selectedFile.name
        }
      />

      <div className="customer-import-container">
        <input
          ref={fileUploadRef}
          className="customer-import-input"
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
        />
        <FontAwesomeIcon className="customer-import-icon" icon={["far", "upload"]} />
      </div>
    </Sheet>
  </Portal>
    ;
}
